import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { getRequest } from 'config/Axios/baseConfig';

// Define interfaces for the room calculation data structure
interface ShiftInfo {
  id: number;
  name: string;
  startTime: string;
  endTime: string;
}

interface ScheduleCalculation {
  id: number;
  jobId: string;
  jobOrder: number;
  cleanId?: string;
  productCalculationId?: number;
  cleanCalculationId?: number;
  startTime?: string;
  duration?: number;
  endTime?: string;
  shift?: ShiftInfo;
  room?: string;
  roomId?: number;
  bottles?: number;
  flag: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface ProductCalculation {
  id: number;
  jobId: string;
  jobOrder?: number;
  drugReferenceFgNDC: string;
  drugReferenceConceptCode?: string;
  description?: string;
  bottlesRequired: number;
  projectedBpm?: number;
  actualBpm?: number;
  pillCount?: number;
  startTime?: string;
  durationMinutes?: number;
  endTime?: string;
  room?: string;
  roomId?: number;
  runType?: string;
  runTypeId?: number;
  flag: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface CleanCalculation {
  id: number;
  cleanId: string;
  incomingJobId: string;
  outgoingJobId: string;
  drug?: string;
  dosage?: number;
  color?: string;
  coated?: {
    id: number | null;
    coatingCode: number | null;
    description: string | null;
  };
  cleanTime: number | string;
  clearTime: number | string;
  setupTime: number | string;
  lvTime: number | string;
  changeoverR2: number | string;
  clean: {
    id: number | null;
    description: string | null;
  };
  cleanOverride: {
    id: number | null;
    description: string | null;
  };
  cleanJobId: string;
  room?: string;
  roomId?: number;
  flag: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface JobData {
  job_id: string;
  job_order: number;
  status: string;
  drug_description: string;
  ndc: string;
  priority: number;
  product_calculation: ProductCalculation | null;
  clean_calculation: CleanCalculation | null;
  schedule_calculation: ScheduleCalculation | null;
}

interface RoomData {
  id: number;
  name: string;
  short_code: string;
  jobs: JobData[];
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

interface CalculationSummary {
  id: number;
  name: string;
  short_code: string;
  job_count: number;
  total_product_minutes: number;
  total_clean_minutes: number;
  total_minutes: number;
}

export class OptimizedCalculationsStore {
  roomCalculations: RoomData[] = [];
  calculationsSummary: CalculationSummary[] = [];
  isLoading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  fetchRoomCalculations = async (date?: string): Promise<void> => {
    this.isLoading = true;
    try {
      const dateQuery = date ? `?date=${date}` : '';
      const { data } = await getRequest<ApiResponse<RoomData[]>>(
        `pps/calculations/optimized/by-room/${dateQuery}`
      );

      runInAction(() => {
        if (data.success) {
          this.roomCalculations = data.data;
        } else {
          this.roomCalculations = [];
          toast.error('Failed to fetch optimized room calculations');
        }
      });
    } catch (error) {
      runInAction(() => {
        this.roomCalculations = [];
        toast.error('Error fetching optimized room calculations');
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  fetchCalculationsSummary = async (date?: string): Promise<void> => {
    this.isLoading = true;
    try {
      const dateQuery = date ? `?date=${date}` : '';
      const { data } = await getRequest<ApiResponse<CalculationSummary[]>>(
        `pps/calculations/optimized/summary/${dateQuery}`
      );

      runInAction(() => {
        if (data.success) {
          this.calculationsSummary = data.data;
        } else {
          this.calculationsSummary = [];
          toast.error('Failed to fetch calculations summary');
        }
      });
    } catch (error) {
      runInAction(() => {
        this.calculationsSummary = [];
        toast.error('Error fetching calculations summary');
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  // Reset store data
  reset = (): void => {
    this.roomCalculations = [];
    this.calculationsSummary = [];
    this.isLoading = false;
  };
}
